import type { DeeplinkAction, DeeplinkAssets } from '@carboncopyco/types';
import { isDesktop } from '@carboncopyco/utils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { usePoc } from './usePoc';

export const DESKTOP_DEEPLINK_PREFIX = 'desktopDeeplink';

const deepLinkToDesktopWithAction = ({
  action,
  organizationId,
  workspaceId,
  assetType,
  assetId,
}: {
  organizationId: string;
  workspaceId: string;
  assetType: DeeplinkAssets;
  action: DeeplinkAction;
  assetId: string | number;
}) => {
  window.open(
    `${window.origin}/${organizationId}/${DESKTOP_DEEPLINK_PREFIX}/${action}/${assetType}/${workspaceId}/${assetId}`,
    '_blank'
  );
};

const extractOrgIdFromPath = (path: string): string | null => {
  const match = path.match(/\d+/);
  return match ? match[0] : null;
};

const deeplinkToDesktopWithPath = (_path: string, searchParams: string) => {
  const { pathname, origin } = window.location;
  const orgId = extractOrgIdFromPath(pathname);
  return window.open(
    `${origin}/${orgId}/${DESKTOP_DEEPLINK_PREFIX}?path=${pathname}${
      searchParams ? `&${searchParams}` : ''
    }`,
    '_blank'
  );
};

export const useDesktopDeeplink = () => {
  const flags = useFlags();
  const isPoc = usePoc();
  const shouldShowDeeplink =
    !isDesktop() &&
    (flags.desktopRedirection || (isPoc && flags.desktopRedirectionPoc));
  const exeDeeplink = (
    deeplinkKey: DeeplinkAction,
    expectedValue = 'true',
    rollout?: () => void
  ) => {
    if (!shouldShowDeeplink) {
      rollout?.();
      return;
    }
    const searchParam = new URLSearchParams({
      [deeplinkKey]: expectedValue,
    });
    deeplinkToDesktopWithPath(window.location.pathname, searchParam.toString());
  };
  const exeDeeplinkWithAction = (
    {
      action,
      organizationId,
      workspaceId,
      assetType,
      assetId,
    }: {
      organizationId: string;
      workspaceId: string;
      assetType: DeeplinkAssets;
      action: DeeplinkAction;
      assetId: string | number;
    },
    rollout: () => void
  ) => {
    if (!shouldShowDeeplink) {
      rollout();
      return;
    }
    deepLinkToDesktopWithAction({
      action,
      organizationId,
      workspaceId,
      assetType,
      assetId,
    });
  };
  return {
    shouldShowDeeplink,
    exeDeeplink,
    exeDeeplinkWithAction,
  };
};
